import { brandingConfig, isTagadaPay } from '@/config/branding';
import { getIntegrationImage } from '@/lib/utils/integration-utils';

const tagadaProcessorLogo = isTagadaPay
  ? '/integrations/tagadapay-logo.png'
  : (brandingConfig.defaultIntegrationLogo ?? brandingConfig.logo);
const sandboxProcessorLogo = brandingConfig.defaultIntegrationLogo ?? brandingConfig.logo;

export type ProcessorMetadataEntry = {
  name: string;
  description: string;
  logo: string;
  color: string;
  lightColor: string;
  isBeta?: boolean;
};

export const PROCESSOR_METADATA: Record<string, ProcessorMetadataEntry> = {
  stripe: {
    name: 'Stripe',
    description: "Accept payments via Stripe's global payment platform",
    logo: '/integrations/stripe-icon.svg',
    color: '#635BFF',
    lightColor: '#F6F5FF',
  },
  airwallex: {
    name: 'Airwallex',
    description: 'Accept payments globally with Airwallex payment platform',
    logo: '/integrations/airwallex-icon.svg',
    color: '#00D4AA',
    lightColor: '#F0FDFA',
  },
  checkout: {
    name: 'Checkout',
    description: "Accept payment with Checkout.com's platform",
    logo: '/integrations/checkout-icon.svg',
    color: '#186AFF',
    lightColor: '#F6F5FF',
  },
  nmi: {
    name: 'NMI',
    description: "Process payments through NMI's payment gateway",
    logo: '/integrations/nmi-icon.png',
    color: '#2D3142',
    lightColor: '#F5F6F7',
  },
  netvalve: {
    name: 'Corepay (NetValve)',
    description:
      "Accept card payments through NetValve, Corepay's gateway. On NMI or Authorize.net with Corepay? Use those connectors.",
    logo: '/integrations/netvalve-icon.svg',
    color: '#0F766E',
    lightColor: '#F0FDFA',
  },
  xoala: {
    name: 'Xoala',
    description: 'Accept card payments through Xoala, with 3-D Secure run by Xoala and card-on-file rebills',
    logo: '/integrations/xoala-icon.svg',
    color: '#4338CA',
    lightColor: '#EEF2FF',
  },
  unlimit: {
    name: 'Unlimit',
    description: 'Accept card payments through Unlimit (formerly Cardpay), with your own Unlimit merchant account.',
    logo: '/integrations/unlimit-icon.svg',
    color: '#4338CA',
    lightColor: '#EEF2FF',
  },
  onyx: {
    name: 'Onyx Processing',
    description: 'Accept payments through Onyx Processing',
    logo: '/integrations/nmi-icon.png',
    color: '#111827',
    lightColor: '#F3F4F6',
  },
  finix: {
    name: 'Finix',
    description: 'Payment processing platform for software platforms',
    logo: '/integrations/finix-icon.jpg',
    color: '#4F46E5',
    lightColor: '#EEF2FF',
  },
  // Platform MOR gateway. Still very much alive: the guided setup creates it
  // programmatically with an express publishable key
  // (`buildSetupTagadaPayProcessorUpdate`). Its manual form, however, asks for
  // an account id nobody hands out self-serve — which is why it is filtered
  // out of the creation grid below while keeping its name here for the
  // processors merchants already run.
  tagadapay: {
    name: 'TagadaPay',
    description: 'Accept payments through TagadaPay Gateway',
    logo: tagadaProcessorLogo,
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  'tagadapay-router': {
    name: 'TagadaPay',
    description: 'Let TagadaPay process your payments as merchant of record',
    logo: tagadaProcessorLogo,
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  sandbox: {
    name: 'Sandbox',
    description: 'Test payment processor for development and testing',
    logo: sandboxProcessorLogo,
    color: '#10B981',
    lightColor: '#F0FDF4',
  },
  mastercard: {
    name: 'Mastercard',
    description: 'Enterprise payment processing with Mastercard Gateway',
    logo: '/integrations/mastercard.svg',
    color: '#FF5F00',
    lightColor: '#FFF5F0',
  },
  trustflow: {
    name: 'Trust Flow',
    description: 'Accept payments through Trust Flow payment gateway',
    logo: '/integrations/trustflow.png',
    color: '#1E40AF',
    lightColor: '#EFF6FF',
  },
  kesspay: {
    name: 'KessPay',
    description: 'Accept payments through KessPay Gateway',
    logo: '/integrations/kesspay-icon.png',
    color: '#FF6B35',
    lightColor: '#FFF2EE',
  },
  ovri: {
    name: 'Ovri',
    description: 'Accept payments through Ovri payment gateway',
    logo: '/integrations/ovri-icon.png',
    color: '#0066CC',
    lightColor: '#E6F2FF',
  },
  ccavenue: {
    name: 'CCAvenue',
    description: 'Accept payments through CCAvenue payment gateway',
    logo: '/integrations/ccavenue-logo.png',
    color: '#009ddc',
    lightColor: '#e2f7ff',
  },
  ngenius: {
    name: 'Network Ngenius',
    description: 'Accept payments across the Middle East and Africa with Network International',
    logo: '/integrations/network-ngenius-icon.png',
    color: '#0069b1',
    lightColor: '#f1f8ff',
  },
  tap: {
    name: 'Tap Payments',
    description: 'Accept payments across the Middle East with Tap payment gateway',
    logo: '/integrations/tap-icon.svg',
    color: '#00D4AA',
    lightColor: '#F0FDFA',
    isBeta: true,
  },
  authorizenet: {
    name: 'Authorize.Net',
    description: 'Accept payments through Authorize.Net payment gateway',
    logo: '/integrations/authorizenet-icon.png',
    color: '#003366',
    lightColor: '#F0F4F8',
    isBeta: true,
  },
  convesio: {
    name: 'ConvesioPay',
    description: 'Accept payments through ConvesioPay payment gateway',
    logo: '/integrations/convesiopay.svg',
    color: '#6366F1',
    lightColor: '#EEF2FF',
    isBeta: true,
  },
  payland: {
    name: 'Payland',
    description: 'Accept card and crypto payments through Payland gateway',
    logo: '/integrations/payland-icon.png',
    color: '#1A56DB',
    lightColor: '#EBF5FF',
  },
  paysafe: {
    name: 'Paysafe',
    description: 'Accept payments globally with Paysafe payment platform',
    logo: '/integrations/paysafe-icon.png',
    color: '#00A651',
    lightColor: '#F0FDF4',
  },
  bluesnap: {
    name: 'BlueSnap',
    description: 'Simplify global payments with BlueSnap payment gateway',
    logo: '/integrations/bluesnap-icon.png',
    color: '#0066CC',
    lightColor: '#E6F2FF',
  },
  worldpay: {
    name: 'Worldpay',
    description: 'Accept payments worldwide with Worldpay payment platform',
    logo: '/integrations/worldpay-icon.png',
    color: '#CC0000',
    lightColor: '#FFF0F0',
  },
  braintree: {
    name: 'Braintree',
    description: 'Accept payments with Braintree, a PayPal service',
    logo: '/integrations/braintree-icon.png',
    color: '#009CDE',
    lightColor: '#E6F7FF',
  },
  mollie: {
    name: 'Mollie',
    description: 'Accept payments across Europe with Mollie payment gateway',
    logo: '/integrations/mollie-icon.png',
    color: '#FF6640',
    lightColor: '#FFF2EF',
  },
  fiserv: {
    name: 'Fiserv',
    description: 'Enterprise payment processing with Fiserv Commerce Hub',
    logo: '/integrations/fiserv-icon.png',
    color: '#FF6600',
    lightColor: '#FFF3E6',
  },
  paystack: {
    name: 'Paystack',
    description: 'Accept payments across Africa with Paystack payment gateway',
    logo: '/integrations/paystack-icon.png',
    color: '#00C3F7',
    lightColor: '#E6F9FF',
  },
  payu: {
    name: 'PayU',
    description: 'Global payment processing in emerging markets with PayU',
    logo: '/integrations/payu-icon.png',
    color: '#7DC243',
    lightColor: '#F3FBEB',
  },
  razorpay: {
    name: 'Razorpay',
    description: "Accept payments in India with Razorpay's payment platform",
    logo: '/integrations/razorpay-icon.png',
    color: '#3395FF',
    lightColor: '#EBF4FF',
  },
  payone: {
    name: 'PAYONE',
    description: 'Accept payments across Europe with PAYONE payment gateway',
    logo: '/integrations/payone-icon.png',
    color: '#FF3300',
    lightColor: '#FFF0EE',
  },
  globalpay: {
    name: 'Global Payments',
    description: 'Enterprise-grade payment processing with Global Payments',
    logo: '/integrations/globalpay-icon.png',
    color: '#0033A0',
    lightColor: '#EEF2FF',
  },
};

export type ProcessorTypeOption = {
  value: string;
  label: string;
  description: string;
  logo: string;
  lightColor: string;
  isBeta: boolean;
};

export const getProcessorBrandSurface = (type: string) => {
  const meta = PROCESSOR_METADATA[type];
  return {
    lightColor: meta?.lightColor ?? '#ffffff',
  };
};

export const PROCESSOR_TYPE_OPTIONS: ProcessorTypeOption[] = Object.entries(PROCESSOR_METADATA).map(
  ([value, meta]) => ({
    value,
    label: meta.name,
    description: meta.description,
    logo: getIntegrationImage(value),
    lightColor: meta.lightColor,
    isBeta: meta.isBeta ?? false,
  }),
);
